import type { WorkspaceOut, components } from "@sagot/api-client";
import { CRAWL_DEFAULT_PAGE_LIMIT, CRAWL_PAGE_LIMITS, CRAWL_URL_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select, toast } from "@sagot/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormAlert } from "../../auth/components/form-alert";
import { splitPatterns } from "../../features/deploy-knowledge/knowledge/website-panel";
import { useCreateSource, useDiscoverSite } from "../../features/deploy-knowledge/knowledge/use-knowledge";
import { useErrorMessage } from "../../lib/api-errors";
import { zodResolver } from "../../lib/zod-resolver";
import { isHttpUrl, planExcludes, selectedPageCount, websiteUrl } from "./crawl-model";
import { CrawlResults } from "./crawl-results";
import type { SourceTargets } from "./use-source-targets";

const K = "onboarding.knowledge";
const KB_ADD = "deployKnowledge.knowledge.add";

type Discovery = components["schemas"]["DiscoveryResult"];

interface Scan {
  url: string;
  maxPages: number;
  include: string[];
  exclude: string[];
  result: Discovery;
}

interface CrawlTabProps {
  workspace: WorkspaceOut | undefined;
  targets: SourceTargets;
}

/**
 * Crawl website: the same discovery preview and website source as the Knowledge Base page. "Fetch
 * pages" shows what a crawl would visit; the crawl starts from that exact snapshot.
 */
export function CrawlTab({ workspace, targets }: CrawlTabProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const discover = useDiscoverSite();
  const create = useCreateSource();
  const [scan, setScan] = useState<Scan | null>(null);
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const schema = z.object({
    url: z.string().trim().min(1, t(`${K}.crawlUrlRequired`)).max(CRAWL_URL_MAX_LENGTH).refine(isHttpUrl, t(`${K}.crawlUrlInvalid`)),
    maxPages: z.coerce.number().int(),
    include: z.string(),
    exclude: z.string(),
  });
  type Values = z.infer<typeof schema>;
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { url: websiteUrl(workspace?.website), maxPages: CRAWL_DEFAULT_PAGE_LIMIT, include: "", exclude: "" },
  });

  const fetchPages = handleSubmit((values) => {
    setError(null);
    const input = { url: values.url, maxPages: values.maxPages, include: splitPatterns(values.include), exclude: splitPatterns(values.exclude) };
    discover.mutate(input, {
      onSuccess: (result) => {
        setScan({ ...input, result });
        setDeselected(new Set());
      },
      onError: () => { setScan(null); },
    });
  });

  const addPages = () => {
    if (!scan) return;
    const plan = planExcludes(scan.exclude, [...deselected]);
    if (plan.tooMany) {
      setError(t(`${K}.crawlTooManyExcludes`));
      return;
    }
    setError(null);
    const source = { type: "website" as const, input: { url: scan.url, maxPages: scan.maxPages, include: scan.include, exclude: plan.patterns, audiences: [...targets.audiences], agentIds: targets.agentIds, schedule: "weekly" as const } };
    create.mutate(source, {
      onSuccess: () => {
        toast.success(t(`${KB_ADD}.crawlQueued`, { count: selectedPageCount(scan.result.page_count, deselected.size) }));
        setScan(null);
        discover.reset();
      },
      onError: (caught) => { setError(message(caught)); },
    });
  };

  const toggle = (url: string, selected: boolean) => {
    setDeselected((current) => {
      const next = new Set(current);
      if (selected) next.delete(url);
      else next.add(url);
      return next;
    });
  };
  const clearAll = () => { setDeselected(new Set((scan?.result.sample ?? []).filter((url) => url !== scan?.result.start_url))); };

  return (
    <form noValidate className="space-y-4" onSubmit={(event) => void fetchPages(event)}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_140px]">
        <Field label={t(`${K}.crawlUrl`)} error={formState.errors.url?.message}>
          <Input type="url" inputMode="url" placeholder="https://" {...register("url")} />
        </Field>
        <Field label={t(`${K}.crawlLimit`)}>
          <Select {...register("maxPages")}>
            {CRAWL_PAGE_LIMITS.map((count) => (
              <option key={count} value={count}>
                {t(`${K}.crawlPages`, { count })}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t(`${K}.crawlInclude`)} hint={t(`${K}.crawlIncludeHint`)}>
          <Input className="font-mono text-[13px]" placeholder="/admissions/*, /enroll" {...register("include")} />
        </Field>
        <Field label={t(`${K}.crawlExclude`)}>
          <Input className="font-mono text-[13px]" placeholder="/news/*, /login" {...register("exclude")} />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="secondary" loading={discover.isPending}>
          <Icon name="search" />
          {discover.isPending ? t(`${K}.crawlFetching`) : t(`${K}.crawlFetch`)}
        </Button>
        <span className="text-xs text-fg-subtle">{t(`${K}.crawlNote`)}</span>
      </div>
      {discover.isError ? (
        <FormAlert title={t(`${K}.crawlFetchFailed`)}>
          {message(discover.error)}
        </FormAlert>
      ) : null}
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
      {scan ? (
        <CrawlResults
          result={scan.result}
          deselected={deselected}
          adding={create.isPending}
          onToggle={toggle}
          onSelectAll={() => { setDeselected(new Set()); }}
          onClear={clearAll}
          onAdd={addPages}
        />
      ) : null}
    </form>
  );
}
